import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Crown, Zap, Star, Award, Check, ArrowUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SubscriptionManager() {
  const { user } = useContext(AuthContext);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription/plans`);
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription plans:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const upgradePlan = async (newPlan) => {
    setUpgrading(true);
    try {
      const response = await axios.post(`${API}/subscription/upgrade`, null, {
        params: { new_plan: newPlan }
      });
      
      toast.success(response.data.message);
      fetchSubscriptionPlans(); // Refresh data
      
      // Refresh user context if needed
      window.location.reload();
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upgrade subscription';
      toast.error(message);
    } finally {
      setUpgrading(false);
    }
  };

  const getPlanIcon = (planName) => {
    switch (planName.toLowerCase()) {
      case 'plus': return <Zap size={32} />;
      case 'superplus': return <Star size={32} />;
      case 'ultraplus': return <Crown size={32} />;
      case 'basic': return <Award size={32} />;
      case 'premium': return <Crown size={32} />;
      default: return <Award size={32} />;
    }
  };

  const getPlanColor = (planName) => {
    const colors = {
      plus: '#06B6D4',
      superplus: '#F59E0B', 
      ultraplus: '#8B5CF6',
      basic: '#6B7280',
      premium: '#10B981'
    };
    return colors[planName.toLowerCase()] || '#6B7280';
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="loading-spinner"></div>
            <p style={{ marginTop: '20px', color: '#718096' }}>Loading subscription plans...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!subscriptionData) {
    return (
      <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#718096' }}>Failed to load subscription plans</p>
          </div>
        </div>
      </div>
    );
  }

  // If property owner, show message that they don't have subscriptions
  if (subscriptionData.message) {
    return (
      <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
        <div className="container">
          <div style={{ 
            background: 'white', 
            borderRadius: '16px', 
            padding: '60px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '24px' }}>🏠</div>
            <h2 style={{ fontSize: '2rem', fontWeight: '600', color: '#2d3748', marginBottom: '16px' }}>
              Property Owner Account
            </h2>
            <p style={{ color: '#718096', fontSize: '1.1rem', marginBottom: '24px' }}>
              Subscription plans are exclusively for travelers. As a property owner, you have full access to list and manage your properties without any subscription fees.
            </p>
            <div style={{ 
              background: '#f0f8ff', 
              padding: '20px', 
              borderRadius: '12px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#2d3748', marginBottom: '12px' }}>Your Benefits Include:</h3>
              <ul style={{ listStyle: 'none', padding: 0, color: '#4a5568' }}>
                <li style={{ padding: '4px 0' }}>✓ Free property listing</li>
                <li style={{ padding: '4px 0' }}>✓ Booking management</li>
                <li style={{ padding: '4px 0' }}>✓ Guest communication</li>
                <li style={{ padding: '4px 0' }}>✓ Earnings tracking</li>
                <li style={{ padding: '4px 0' }}>✓ 10% service fee only on bookings</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isPropertyOwner = user.role === 'property_owner';

  return (
    <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ 
            fontSize: '3rem', 
            fontWeight: '700', 
            color: '#2d3748',
            marginBottom: '16px'
          }}>
            {subscriptionData.plan_type}
          </h1>
          <p style={{ 
            fontSize: '1.25rem', 
            color: '#718096',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Choose the perfect plan to maximize your travel rewards and unlock exclusive perks!
          </p>
        </div>

        {/* Current Plan Status */}
        <div style={{ 
          background: 'white', 
          borderRadius: '16px', 
          padding: '24px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          marginBottom: '40px',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
            Current Plan
          </h3>
          <div style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '12px',
            background: '#f7fafc',
            padding: '12px 24px',
            borderRadius: '12px'
          }}>
            <div style={{ color: getPlanColor(subscriptionData.current_plan) }}>
              {getPlanIcon(subscriptionData.current_plan)}
            </div>
            <span style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600', 
              color: '#2d3748',
              textTransform: 'capitalize'
            }}>
              {subscriptionData.current_plan.replace('_', '')} Plan
            </span>
          </div>
        </div>

        {/* Subscription Plans */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '24px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {subscriptionData.available_plans.map((plan, index) => {
            const isCurrentPlan = subscriptionData.current_plan.includes(plan.name.toLowerCase()) ||
                                 subscriptionData.current_plan === plan.name.toLowerCase().replace('super', 'super_').replace('ultra', 'ultra_');
            const isPopular = plan.name === 'SuperPlus';
            
            return (
              <div 
                key={index}
                style={{
                  background: isCurrentPlan ? `linear-gradient(135deg, ${plan.color}, ${plan.color}dd)` : 'white',
                  color: isCurrentPlan ? 'white' : '#2d3748',
                  borderRadius: '20px',
                  padding: '32px',
                  boxShadow: isCurrentPlan ? `0 20px 40px ${plan.color}40` : '0 10px 30px rgba(0, 0, 0, 0.1)',
                  border: isCurrentPlan ? 'none' : '2px solid #e2e8f0',
                  position: 'relative',
                  transform: isPopular ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.3s ease'
                }}
              >
                {isPopular && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#F59E0B',
                    color: 'white',
                    padding: '6px 16px',
                    borderRadius: '12px',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}>
                    MOST POPULAR
                  </div>
                )}

                {isCurrentPlan && (
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    CURRENT
                  </div>
                )}

                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <div style={{ 
                    margin: '0 auto 16px',
                    color: isCurrentPlan ? 'white' : plan.color,
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    {getPlanIcon(plan.name)}
                  </div>
                  
                  <h3 style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: '700', 
                    marginBottom: '8px' 
                  }}>
                    {plan.name}
                  </h3>
                  
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '4px' }}>
                    ${plan.price}
                  </div>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    opacity: isCurrentPlan ? 0.9 : 0.7,
                    marginBottom: '8px'
                  }}>
                    per month
                  </div>
                  
                  <p style={{ 
                    fontSize: '0.95rem', 
                    opacity: isCurrentPlan ? 0.9 : 0.8,
                    fontWeight: '500'
                  }}>
                    {plan.description}
                  </p>
                </div>

                {/* Features */}
                <div style={{ marginBottom: '24px' }}>
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      marginBottom: '8px'
                    }}>
                      <Check size={16} style={{ 
                        color: isCurrentPlan ? 'white' : '#10B981',
                        flexShrink: 0
                      }} />
                      <span style={{ fontSize: '0.9rem' }}>{feature}</span>
                    </div>
                  ))}
                  
                  {/* Loyalty Multiplier for all plans */}
                  {plan.loyalty_multiplier && (
                    <div style={{
                      marginTop: '16px',
                      padding: '12px',
                      background: isCurrentPlan ? 'rgba(255, 255, 255, 0.1)' : '#f0f8ff',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ 
                        fontSize: '1.5rem', 
                        fontWeight: '700',
                        color: isCurrentPlan ? 'white' : (plan.loyalty_multiplier > 1 ? '#F59E0B' : '#6B7280'),
                        marginBottom: '4px'
                      }}>
                        {plan.loyalty_multiplier}X
                      </div>
                      <div style={{ 
                        fontSize: '0.875rem',
                        opacity: isCurrentPlan ? 0.9 : 0.7
                      }}>
                        Loyalty Points
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Button */}
                <button 
                  onClick={() => upgradePlan(plan.name.toLowerCase().replace('super', 'super_').replace('ultra', 'ultra_'))}
                  disabled={isCurrentPlan || upgrading}
                  className="btn"
                  style={{ 
                    width: '100%',
                    padding: '12px',
                    fontSize: '1rem',
                    background: isCurrentPlan ? 'rgba(255, 255, 255, 0.2)' : (isPopular ? '#F59E0B' : '#10B981'),
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: isCurrentPlan ? 'default' : 'pointer',
                    opacity: isCurrentPlan ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {isCurrentPlan ? (
                    'Current Plan'
                  ) : (
                    <>
                      <ArrowUp size={16} />
                      {upgrading ? 'Upgrading...' : 'Upgrade Now'}
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Benefits Comparison */}
        <div style={{ 
          background: 'white', 
          borderRadius: '16px', 
          padding: '32px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          marginTop: '40px'
        }}>
          <h3 style={{ 
            fontSize: '1.75rem', 
            fontWeight: '600', 
            textAlign: 'center',
            marginBottom: '32px',
            color: '#2d3748'
          }}>
            Loyalty Points Example
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '20px',
            textAlign: 'center'
          }}>
            <div style={{ padding: '20px', background: '#f7fafc', borderRadius: '12px' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                $300 Booking
              </div>
              <div style={{ fontSize: '0.875rem', color: '#718096' }}>Example booking amount</div>
            </div>
            
            <div style={{ padding: '20px', background: '#e0f7fa', borderRadius: '12px' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#06B6D4', marginBottom: '8px' }}>
                Plus: 600 pts
              </div>
              <div style={{ fontSize: '0.875rem', color: '#718096' }}>2x multiplier</div>
            </div>
            
            <div style={{ padding: '20px', background: '#fef3c7', borderRadius: '12px' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#F59E0B', marginBottom: '8px' }}>
                SuperPlus: 900 pts
              </div>
              <div style={{ fontSize: '0.875rem', color: '#718096' }}>3x multiplier</div>
            </div>
            
            <div style={{ padding: '20px', background: '#f3e8ff', borderRadius: '12px' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#8B5CF6', marginBottom: '8px' }}>
                UltraPlus: 1,500 pts
              </div>
              <div style={{ fontSize: '0.875rem', color: '#718096' }}>5x multiplier</div>
            </div>

            <div style={{ padding: '20px', background: '#f3f4f6', borderRadius: '12px' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#10B981', marginBottom: '8px' }}>
                Basic: 300 pts + AI
              </div>
              <div style={{ fontSize: '0.875rem', color: '#718096' }}>1x standard + Concierge</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionManager;